import classNames from "classnames";
import filter from "lodash/filter";
import head from "lodash/head";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import isNaN from "lodash/isNaN";
import isNumber from "lodash/isNumber";
import isUndefined from "lodash/isUndefined";
import toNumber from "lodash/toNumber";
import trimStart from "lodash/trimStart";
import words from "lodash/words";
import { AccountAvailability, FeatureFlag, Id } from "PFTypes";
import {
  AvailabilityMode,
  AvailabilityRange,
  AvailabilityRule,
  DurationMode,
  StandardRange,
  TimeRuleRange
} from "PFTypes/booking";
import { Phase } from "PFTypes/phase";
import { createRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import useIsFeatureEnabled from "../../../helpers/use_is_feature_enabled";
import { Tile, TileGroup } from "../../tile";
import { RANGES_ERROR_KEY } from "../constants";
import { getErrorsForRuleIndex } from "../errors_helper";
import { LoadEstimationData, useLoadEstimation } from "../hooks/use_load_estimation";
import { RulesCarouselToolbar } from "../rules_carousel_toolbar";
import { PhasesSelect } from "./phases_select";
import css from "./rules_carousel.module.scss";
import { StandardRule } from "./standard_rule";
import { TimeRule } from "./time_rule";

type RulesCarouselProps = {
  accountAvailability: AccountAvailability;
  defaultAvailabilityRule: AvailabilityRule;
  isTimeRule: boolean;
  allowedModes: AvailabilityMode[];
  allowedDurationModes: DurationMode[];
  availability: Partial<AvailabilityRule>;
  minDate?: { start: string; end?: string };
  activityId?: Id;
  onChange: (availability: Partial<AvailabilityRule>) => void;
  errors?: Record<string, string>;
  portalRef?: React.RefObject<HTMLDivElement | null>;
  isDisabled?: boolean;
  isValidating?: boolean;
  allowPhases?: boolean;
};

export const RulesCarousel = ({
  availability,
  defaultAvailabilityRule,
  minDate,
  isTimeRule,
  allowedModes,
  allowedDurationModes,
  accountAvailability,
  activityId,
  errors = {},
  onChange,
  portalRef,
  isDisabled,
  isValidating,
  allowPhases
}: RulesCarouselProps) => {
  const { t } = useTranslation();

  const hasActivityPhases = useIsFeatureEnabled(FeatureFlag.ActivityPhases) && allowPhases;

  const [carouselIndex, setCarouselIndex] = useState(0);
  const [slideLeft, setSlideLeft] = useState(true);
  const [rules, setRules] = useState<AvailabilityRange[]>(
    availability.ranges || defaultAvailabilityRule.ranges
  );
  const [isAnimating, setIsAnimating] = useState(false);

  const availabilityMode = availability.mode || defaultAvailabilityRule.mode;
  const { getLoadEstimationData } = useLoadEstimation(isTimeRule, availabilityMode);

  useEffect(() => {
    if (availability.ranges && !isEqual(availability.ranges, rules)) {
      setRules(availability.ranges);
      setCarouselIndex(0);
    }
  }, [availability.ranges]);

  useEffect(() => {
    if (isUndefined(availability.ranges)) {
      setRules(defaultAvailabilityRule.ranges);
      setCarouselIndex(0);
    }
  }, [availability, defaultAvailabilityRule, isTimeRule]);

  const handleChange = (rules: AvailabilityRange[]) => {
    onChange({ ...availability, ranges: rules } as AvailabilityRule);
  };

  useEffect(() => {
    if (errors && !isEmpty(errors)) {
      const errorKeys = Object.keys(errors);
      const errorIndexes = filter(
        errorKeys.map((key) => toNumber(head(words(trimStart(key, RANGES_ERROR_KEY))))),
        (index) => isNumber(index) && !isNaN(index)
      );

      if (!isEmpty(errorIndexes)) {
        setCarouselIndex(Math.min(...errorIndexes));
      }
    }
  }, [errors]);

  const handleNextRule = () => {
    setSlideLeft(true);
    setCarouselIndex((prev) => Math.min(prev + 1, rules.length - 1));
  };

  const handlePrevRule = () => {
    setSlideLeft(false);
    setCarouselIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNewRule = () => {
    setRules((prev) => {
      const newRules = [...prev];
      newRules.push(defaultAvailabilityRule.ranges[0]);
      setCarouselIndex(newRules.length - 1);

      handleChange(newRules);
      return newRules;
    });
  };

  const handleRemoveRule = (index) => {
    setRules((prev) => {
      const newRules = [...prev];
      newRules.splice(index, 1);
      setCarouselIndex(Math.min(index, newRules.length - 1));

      handleChange(newRules);
      return newRules;
    });
  };

  const handleUpdateRule = (index: number, range: AvailabilityRange, update: Partial<AvailabilityRange>) => {
    setRules((prev) => {
      const newRules = [...prev];
      newRules[index] = { ...range, ...update } as AvailabilityRange;

      handleChange(newRules);
      return newRules;
    });
  };

  const handleModeChange = (mode) => {
    onChange({ ...availability, mode });
  };

  const handlePhaseChange = (index, range, { sourceId }: Phase) => {
    handleUpdateRule(index, range, {
      phase_source_id: sourceId
    });
  };

  const renderTimeRule = (range: TimeRuleRange, loadData: LoadEstimationData, index: number) => (
    <TimeRule
      key={index}
      range={range}
      onChange={(update) => handleUpdateRule(index, range, update)}
      minDate={minDate}
      errors={getErrorsForRuleIndex(errors, index, true)}
      portalRef={portalRef}
      loadData={loadData}
      isDisabled={isDisabled}
    />
  );

  const renderStandardRule = (range: StandardRange, loadData: LoadEstimationData, index: number) => (
    <StandardRule
      key={index}
      range={range}
      onChange={(update) => handleUpdateRule(index, range, update)}
      minDate={minDate}
      errors={getErrorsForRuleIndex(errors, index, true)}
      mode={availabilityMode}
      allowedModes={allowedModes}
      allowedDurationModes={allowedDurationModes}
      accountAvailability={accountAvailability}
      onModeChange={index === 0 ? handleModeChange : undefined}
      portalRef={portalRef}
      loadData={loadData}
      isDisabled={isDisabled}
    />
  );

  const renderRule = (range: AvailabilityRange, loadData: LoadEstimationData, index: number) =>
    isTimeRule
      ? renderTimeRule(range as TimeRuleRange, loadData, index)
      : renderStandardRule(range as StandardRange, loadData, index);

  const ranges = isUndefined(availability.ranges) ? defaultAvailabilityRule.ranges : rules;

  const carouselItems = ranges.map((range: AvailabilityRange, index) => {
    if (
      (isTimeRule && !(range as TimeRuleRange).day_of_week) ||
      (!isTimeRule && !(range as StandardRange).duration)
    ) {
      return null;
    }

    const itemRef = createRef<null>();
    const loadData = getLoadEstimationData(range);

    return (
      <CSSTransition
        key={index}
        nodeRef={itemRef}
        classNames={{
          enter: classNames({ [css.enterRight]: !slideLeft, [css.enterLeft]: slideLeft }),
          enterActive: css.enterActive,
          exit: css.exit,
          exitActive: css.exitActive
        }}
        timeout={{ enter: 500, exit: 300 }}
        onEnter={() => setIsAnimating(true)}
        onEntered={() => setIsAnimating(false)}
      >
        <div ref={itemRef} className={css.transitionContainer}>
          {hasActivityPhases && (
            <PhasesSelect
              label={t("availabilityRequirement.phasesSelectLabel")}
              className={css.phasesSelect}
              selectedPhaseSourceId={range.phase_source_id}
              activityId={activityId}
              onChange={(phase) => handlePhaseChange(index, range, phase)}
              errors={getErrorsForRuleIndex(errors, index)}
              portalRef={portalRef}
              isDisabled={isDisabled}
            />
          )}
          {renderRule(range, loadData, index)}
        </div>
      </CSSTransition>
    );
  });

  return (
    <div className={classNames(css.rules, { [css.rulesAnimating]: isAnimating })}>
      <TileGroup>
        <Tile border>
          <RulesCarouselToolbar
            onNext={handleNextRule}
            onPrev={handlePrevRule}
            onCreate={handleNewRule}
            onRemove={handleRemoveRule}
            carouselIndex={carouselIndex}
            rules={rules}
            isDisabled={isDisabled}
            hasErrors={!isEmpty(errors)}
            isValidating={isValidating}
          />
        </Tile>
        <Tile border>
          {rules.length === 0 && <div className={css.empty}>{t("availabilityRequirement.noTimeRules")}</div>}
          {!!rules.length && <TransitionGroup>{carouselItems[carouselIndex]}</TransitionGroup>}
        </Tile>
      </TileGroup>
    </div>
  );
};
